import { Sparkles } from 'lucide-react';
import React from 'react';
import { toast } from 'sonner';

import { AgentFlowHistoryButton } from '@/components/agentflow';
import { FaxWizard } from '@/components/fax/wizard/FaxWizard';
import {
  LegacyLOAChaserSection,
  LOAChaserWidget,
  LOAChaseStatusCard,
  TaskActivityTimeline,
} from '@/components/LOAChaser';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogTitle,
  RightDialogContent,
} from '@/components/ui/dialog';
import {
  useGetTasksQuery,
  useGetTaskStatusesQuery,
  useUpdateTaskMutation,
} from '@/services/api';
import { useGetCoverageQuery } from '@/services/api/coverageService';
import type { Task } from '@/services/types/client-intake-types';
import {
  CanonicalTaskStatus,
  CanonicalTaskType,
} from '@/services/types/task-types';

import { useLorDocuments } from '../lor/hooks/useLorData';
import { getLatestCoverageTaskByCanonicalType } from '../premises/premisesFlow';
import { CoverageDeliverableMatches } from './CoverageDeliverableMatches';

type Props = {
  matterId: string;
  coverageId: string;
};

export const ClaimAcknowledgementStep: React.FC<Props> = ({
  matterId,
  coverageId,
}) => {
  const { data: coverage } = useGetCoverageQuery({ matterId, coverageId });
  const defaultQuery = React.useMemo(() => {
    const claims = (coverage?.claims || []) as Array<{
      claim_identifier?: string | null;
    }>;
    const firstNonEmpty = (claims || []).find(
      (c) => !!(c?.claim_identifier || '').trim(),
    );
    return (firstNonEmpty?.claim_identifier || '').trim();
  }, [coverage]);

  const { lorDocs } = useLorDocuments(matterId);
  const [isFaxWizardOpen, setIsFaxWizardOpen] = React.useState<boolean>(false);
  const [isCustomChaserOpen, setIsCustomChaserOpen] =
    React.useState<boolean>(false);

  // Task-related hooks for "Mark Acknowledged" functionality
  const { data: tasksResponse } = useGetTasksQuery({
    matter: matterId,
    limit: 200,
  });
  const { data: taskStatuses = [] } = useGetTaskStatusesQuery();
  const [updateTask] = useUpdateTaskMutation();

  // Find the LOR Acknowledgement task for this coverage
  const ackTask = React.useMemo(() => {
    const tasks = (tasksResponse?.results as Task[]) || [];
    return getLatestCoverageTaskByCanonicalType(
      tasks,
      coverageId,
      CanonicalTaskType.LOR_ACKNOWLEDGEMENT,
    );
  }, [tasksResponse?.results, coverageId]);

  // Find the SEND_LOR task for this coverage (needed for email deliverables)
  const sendLorTask = React.useMemo(() => {
    const tasks = (tasksResponse?.results as Task[]) || [];
    return getLatestCoverageTaskByCanonicalType(
      tasks,
      coverageId,
      CanonicalTaskType.SEND_LOR,
    );
  }, [tasksResponse?.results, coverageId]);

  // Check if ack task is completed
  const isAckCompleted = React.useMemo(() => {
    return ackTask?.status?.canonical_status === CanonicalTaskStatus.COMPLETED;
  }, [ackTask]);

  const handleMarkAckReceived = async () => {
    if (!ackTask) return;
    const completedStatus = taskStatuses.find(
      (s) => s.canonical_status === CanonicalTaskStatus.COMPLETED,
    );
    if (!completedStatus) return;
    try {
      await updateTask({
        matterId,
        taskId: ackTask.id,
        task: {
          status_id: completedStatus.id,
          completed_at: new Date().toISOString(),
        },
      }).unwrap();
      toast.success('Acknowledgement marked as received');
    } catch {
      toast.error('Failed to mark acknowledgement as received');
    }
  };

  // Get carrier phone number for voice AI call
  const carrierPhone = React.useMemo(() => {
    const handles = coverage?.carrier?.handles as
      | Array<{ handle_type?: string; value?: string }>
      | undefined;
    if (!Array.isArray(handles)) return null;
    const phone = handles.find(
      (h) =>
        String(h?.handle_type || '')
          .toUpperCase()
          .includes('PHONE') ||
        String(h?.handle_type || '')
          .toUpperCase()
          .includes('VOICE'),
    );
    return phone?.value || null;
  }, [coverage]);

  // Get adjuster email for new email thread (fallback to carrier email if no adjuster email)
  const adjusterEmail = React.useMemo(() => {
    // First, try to find an email from adjusters
    const adjusters = (
      coverage as unknown as {
        adjusters?: Array<{
          handles?: Array<{ handle_type?: string; value?: string }>;
        }>;
      }
    )?.adjusters;
    if (Array.isArray(adjusters) && adjusters.length > 0) {
      const adjusterEmailHandle = adjusters
        .flatMap((adjuster) => adjuster.handles || [])
        .find((h) =>
          String(h?.handle_type || '')
            .toUpperCase()
            .includes('EMAIL'),
        );
      if (adjusterEmailHandle?.value) {
        return String(adjusterEmailHandle.value);
      }
    }

    // Fallback: try to find an email from carrier handles
    const carrierHandles = coverage?.carrier?.handles as
      | Array<{ handle_type?: string; value?: string }>
      | undefined;
    if (Array.isArray(carrierHandles)) {
      const carrierEmailHandle = carrierHandles.find((h) =>
        String(h?.handle_type || '')
          .toUpperCase()
          .includes('EMAIL'),
      );
      if (carrierEmailHandle?.value) {
        return String(carrierEmailHandle.value);
      }
    }

    return null;
  }, [coverage]);

  // Get adjuster phone for dialpad calls
  const adjusterPhone = React.useMemo(() => {
    const adjusters = (
      coverage as unknown as {
        adjusters?: Array<{
          handles?: Array<{ handle_type?: string; value?: string }>;
        }>;
      }
    )?.adjusters;
    if (!Array.isArray(adjusters) || adjusters.length === 0) return null;
    // Look through all adjusters for a phone handle
    const phoneHandle = adjusters
      .flatMap((adjuster) => adjuster.handles || [])
      .find(
        (h) =>
          String(h?.handle_type || '')
            .toUpperCase()
            .includes('PHONE') ||
          String(h?.handle_type || '')
            .toUpperCase()
            .includes('VOICE'),
      );
    return phoneHandle?.value ? String(phoneHandle.value) : null;
  }, [coverage]);

  return (
    <div className="space-y-4">
      {/* Primary Custom LOA Chase Button - prominently positioned */}
      <div className="flex justify-end gap-2 mb-2">
        <Button
          size="sm"
          variant="default"
          onClick={() => setIsCustomChaserOpen(true)}
          disabled={!ackTask?.id}
          title={!ackTask?.id ? 'No acknowledgement task found' : undefined}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Sparkles className="mr-2 h-3 w-3" />
          Custom LOA Chase
        </Button>
      </div>

      {/* LOA Chase Status - shows execution history and plan */}
      {ackTask?.id && (
        <LOAChaseStatusCard
          matterId={matterId}
          coverageId={coverageId}
          taskId={ackTask.id}
          mode="full"
          hideTaskUpdates
        />
      )}

      {/* Legacy LOA Chaser - collapsible section */}
      <LegacyLOAChaserSection
        matterId={matterId}
        coverageId={coverageId}
        claimNumber={defaultQuery}
        taskId={ackTask?.id}
        defaultPhoneNumber={carrierPhone}
        defaultAdjusterPhone={adjusterPhone}
        defaultRecipientEmail={adjusterEmail}
      />

      {/* Custom LOA Chaser Modal - AgentFlow mode (controlled by button below) */}
      {ackTask?.id && (
        <LOAChaserWidget
          matterId={matterId}
          coverageId={coverageId}
          claimNumber={defaultQuery}
          taskId={ackTask.id}
          launchMode="agentflow"
          hideLauncherButton
          launcherOpen={isCustomChaserOpen}
          onLauncherOpenChange={setIsCustomChaserOpen}
          existingCampaign={null}
          defaultPhoneNumber={carrierPhone}
          defaultAdjusterPhone={adjusterPhone}
          defaultRecipientEmail={adjusterEmail}
        />
      )}

      {/* Missing carrier contact warning */}
      {!(
        coverage?.carrier?.handles &&
        (coverage.carrier.handles as unknown as Array<unknown>)?.length
      ) && (
        <div className="rounded border border-red-200 bg-red-50 p-2">
          <div className="text-xs font-semibold text-red-800">
            Missing carrier contact
          </div>
          <div className="text-[11px] text-red-700 mt-1">
            <p>
              Attach a carrier contact handle (phone, email, fax, website) to
              this carrier.
            </p>
            <p>
              Use the Carrier Directory to select the correct division and
              handles.
            </p>
          </div>
        </div>
      )}

      {/* Deliverable matches for coverage tasks */}
      <CoverageDeliverableMatches
        matterId={matterId}
        tasks={[
          ...(sendLorTask
            ? [{ id: sendLorTask.id, label: 'Send LOR', type: 'send_lor' as const }]
            : []),
          ...(ackTask
            ? [{ id: ackTask.id, label: 'LOA Acknowledgement', type: 'ack' as const }]
            : []),
        ]}
      />

      {/* Task Updates - Collapsible timeline */}
      {ackTask?.id && (
        <TaskActivityTimeline
          matterId={matterId}
          taskId={ackTask.id}
          title="Task Updates"
          defaultExpanded={false}
          limit={15}
        />
      )}

      {/* Action buttons */}
      <div className="flex justify-end gap-2">
        {ackTask && !isAckCompleted && (
          <Button size="sm" variant="outline" onClick={handleMarkAckReceived}>
            Mark Acknowledged
          </Button>
        )}
        {ackTask?.id && (
          <AgentFlowHistoryButton
            matterId={matterId}
            coverageId={coverageId}
            taskId={ackTask.id}
            variant="ghost"
            size="sm"
            label="View History"
          />
        )}
        <Button
          size="sm"
          variant="default"
          onClick={() => setIsFaxWizardOpen(true)}
        >
          Send LOR Followup Fax
        </Button>
      </div>

      {/* Fax Wizard Dialog */}
      <Dialog open={isFaxWizardOpen} onOpenChange={setIsFaxWizardOpen}>
        <RightDialogContent className="bg-white h-[95vh] overflow-hidden flex flex-col">
          <DialogTitle className="sr-only">Send Fax</DialogTitle>
          <div className="flex-1 overflow-auto">
            <FaxWizard
              matterId={matterId}
              initialData={{
                recipient_number: (() => {
                  // Prefer adjuster fax; fall back to carrier fax (from selected division handles)
                  try {
                    const adjusters = (
                      coverage as unknown as {
                        adjusters?: Array<{
                          handles?: Array<{
                            handle_type?: string;
                            value?: string;
                          }>;
                        }>;
                      }
                    )?.adjusters;
                    const handles =
                      Array.isArray(adjusters) && adjusters[0]?.handles;
                    if (Array.isArray(handles)) {
                      const fax = handles.find((h) =>
                        String(h.handle_type || '')
                          .toUpperCase()
                          .includes('FAX'),
                      );
                      if (fax?.value) return String(fax.value);
                    }
                  } catch {
                    // no-op
                  }

                  try {
                    const ch = (
                      coverage as unknown as {
                        carrier?: {
                          handles?: Array<{
                            handle_type?: string;
                            value?: string;
                          }>;
                        };
                      }
                    )?.carrier?.handles as
                      | Array<{ handle_type?: string; value?: string }>
                      | undefined;
                    const fax = (Array.isArray(ch) ? ch : []).find((h) =>
                      String(h?.handle_type || '')
                        .toUpperCase()
                        .includes('FAX'),
                    );
                    if (fax?.value) return String(fax.value);
                  } catch {
                    // no-op
                  }
                  return '';
                })(),
                attachment_blob_ids: [],
                attachment_document_ids: (lorDocs || []).map((d) => d.id),
              }}
              documentItems={(lorDocs || []).map((d) => ({
                id: d.id,
                title: d.document_template?.name || 'Letter of Representation',
                createdAt: d.created_at,
              }))}
              onSuccess={() => {
                setIsFaxWizardOpen(false);
                toast.success('Fax flow completed');
              }}
              onCancel={() => setIsFaxWizardOpen(false)}
            />
          </div>
        </RightDialogContent>
      </Dialog>
    </div>
  );
};

export default ClaimAcknowledgementStep;
